import React, { useEffect, useState, useMemo, useRef } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminDirectory = ({ userData }) => {
  const [viewType, setViewType] = useState('submissions'); // 'assignments', 'submissions', 'projects', or 'projectSubmissions'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createdByFilter, setCreatedByFilter] = useState(''); // account_id to filter by project/assignment creator
  const [creators, setCreators] = useState([]);
  const [projectSubFilter, setProjectSubFilter] = useState('all'); // 'all' | 'needsEvaluation' | 'inProgress' | 'completed'
  const [assignmentSubFilter, setAssignmentSubFilter] = useState('all'); // 'all' | 'requireGrading' | 'survey' | 'graded'
  const hasSetDefaultFiltersRef = useRef(false);

  // Store all the fetched data (loaded once)
  const [allAssignments, setAllAssignments] = useState([]);
  const [allAssignmentSubmissions, setAllAssignmentSubmissions] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allProjectSubmissions, setAllProjectSubmissions] = useState([]);
  const [evaluationComments, setEvaluationComments] = useState([]);
  const [flagComments, setFlagComments] = useState([]);

  const fetchCreators = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/admin-directory/creators', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.creators) setCreators(data.creators);
    } catch (err) {
      console.warn('Could not fetch creators:', err);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/admin-directory/data', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch admin directory data');
      }

      setAllAssignments(data.assignments || []);
      setAllAssignmentSubmissions(data.assignmentSubmissions || []);
      setAllProjects(data.projects || []);
      setAllProjectSubmissions(data.projectSubmissions || []);
      setEvaluationComments(data.evaluationComments || []);
      setFlagComments(data.flagComments || []);
    } catch (err) {
      console.error('Error fetching admin directory data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreators();
    fetchAllData();
  }, []);

  // Client-side filter: derive filtered data and counts from loaded data
  const { myAssignments, assignmentSubmissions, myProjects, projectSubmissions, counts, flaggedProjects, evaluationRequiredSubmissions } = useMemo(() => {
    const filterByCreator = (item) => {
      if (!createdByFilter) return true;
      const creatorId = item.createdby ?? item.creator?.account_id ?? item.assignment_createdby ?? item.assignment_creator?.account_id ?? item.project_createdby ?? item.project_creator?.account_id;
      return creatorId === createdByFilter;
    };

    const filteredAssignments = allAssignments.filter(filterByCreator);
    const filteredProjects = allProjects.filter(filterByCreator);
    const filteredAssignmentSubmissions = allAssignmentSubmissions.filter(s => {
      const creatorId = s.assignment_createdby ?? s.assignment_creator?.account_id;
      return !createdByFilter || creatorId === createdByFilter;
    });
    const filteredProjectSubmissions = allProjectSubmissions.filter(ps => {
      const creatorId = ps.project_createdby ?? ps.project_creator?.account_id;
      return !createdByFilter || creatorId === createdByFilter;
    });

    const submissionsRequiringGrading = filteredAssignmentSubmissions.filter(s => s.graderesult === 'needsEvaluation').length;

    const concentrationCapstoneSubmissions = filteredProjectSubmissions.filter(ps => {
      const isConcentrationOrCapstone = ps.type === 'Concentration' || ps.type === 'Capstone';
      const isStarted = ps.status === 'started';
      return isConcentrationOrCapstone && isStarted;
    });

    let projectSubmissionsWaitingForEvaluation = 0;
    const evaluationRequiredIds = new Set();
    for (const ps of concentrationCapstoneSubmissions) {
      const evaluationCommentsForPs = evaluationComments.filter(c => String(c.reference_id) === String(ps.id));
      if (evaluationCommentsForPs.length === 0) {
        projectSubmissionsWaitingForEvaluation++;
        evaluationRequiredIds.add(ps.id);
      }
    }

    let projectsWithUnresolvedFlags = 0;
    const flaggedIds = new Set();
    for (const project of filteredProjects) {
      const flagCommentsForProject = flagComments.filter(c => String(c.reference_id) === String(project.id));
      const unresolvedFlags = flagCommentsForProject.filter(comment => comment.status !== 'resolved');
      if (unresolvedFlags.length > 0) {
        projectsWithUnresolvedFlags++;
        flaggedIds.add(project.id);
      }
    }

    return {
      myAssignments: filteredAssignments,
      assignmentSubmissions: filteredAssignmentSubmissions,
      myProjects: filteredProjects,
      projectSubmissions: filteredProjectSubmissions,
      counts: {
        assignments: filteredAssignments.length,
        submissions: filteredAssignmentSubmissions.length,
        submissionsRequiringGrading,
        projects: filteredProjects.length,
        projectsWithUnresolvedFlags,
        projectSubmissions: filteredProjectSubmissions.length,
        projectSubmissionsWaitingForEvaluation
      },
      flaggedProjects: flaggedIds,
      evaluationRequiredSubmissions: evaluationRequiredIds
    };
  }, [allAssignments, allAssignmentSubmissions, allProjects, allProjectSubmissions, evaluationComments, flagComments, createdByFilter]);

  // Default to "needs attention" filters when data loads and there are any
  useEffect(() => {
    if (loading) return;
    if (hasSetDefaultFiltersRef.current) return;
    hasSetDefaultFiltersRef.current = true;
    if (counts.projectSubmissionsWaitingForEvaluation > 0) setProjectSubFilter('needsEvaluation');
    if (counts.submissionsRequiringGrading > 0) setAssignmentSubFilter('requireGrading');
  }, [loading, counts.projectSubmissionsWaitingForEvaluation, counts.submissionsRequiringGrading]);

  const getCreatorName = (creatorObj) => {
    if (!creatorObj || (typeof creatorObj === 'object' && !creatorObj.account_id && !creatorObj.first_name && !creatorObj.last_name)) return '—';
    const name = [creatorObj.first_name, creatorObj.last_name].filter(Boolean).join(' ').trim();
    return name || creatorObj.primary_email || 'Unknown';
  };

  if (loading && !allAssignments.length && !allProjects.length) return <LoadingSpinner />;
  if (error) return <div style={{ color: 'red', padding: 24 }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <h1>Admin Directory</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>Universal view of all assignments, submissions, projects, and project submissions.</p>

      {/* Filter by creator */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Filter by creator:</label>
        <select
          value={createdByFilter}
          onChange={(e) => setCreatedByFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            minWidth: 200,
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: 14
          }}
        >
          <option value="">All creators</option>
          {creators.map((c) => (
            <option key={c.account_id} value={c.account_id}>
              {c.name} {c.email ? `(${c.email})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Navigation Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 16,
        marginBottom: 32
      }}>
        <div
          onClick={() => setViewType('assignments')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'assignments' ? '#f0f8ff' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'assignments' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'assignments' ? '#1976d2' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1976d2' }}>
            📝 All Assignments
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.assignments} assignment{counts.assignments !== 1 ? 's' : ''}
          </div>
        </div>

        <div
          onClick={() => setViewType('submissions')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'submissions' ? '#fff3e0' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'submissions' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'submissions' ? '#f57c00' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#f57c00' }}>
            📋 Assignment Submissions
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.submissions} total
            {counts.submissionsRequiringGrading > 0 && (
              <span style={{ color: '#d32f2f', fontWeight: 500, marginLeft: 8 }}>
                ({counts.submissionsRequiringGrading} Require Grading)
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => setViewType('projects')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'projects' ? '#e8f5e9' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'projects' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'projects' ? '#388e3c' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#388e3c' }}>
            🚩 All Projects
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.projects} project{counts.projects !== 1 ? 's' : ''}
            {counts.projectsWithUnresolvedFlags > 0 && (
              <span style={{ color: '#d32f2f', fontWeight: 500, marginLeft: 8 }}>
                ({counts.projectsWithUnresolvedFlags} Unresolved Flags)
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => setViewType('projectSubmissions')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'projectSubmissions' ? '#f3e5f5' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'projectSubmissions' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'projectSubmissions' ? '#7b1fa2' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#7b1fa2' }}>
            📊 Project Submissions
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.projectSubmissions} total
            {counts.projectSubmissionsWaitingForEvaluation > 0 && (
              <span style={{ color: '#f57c00', fontWeight: 500, marginLeft: 8 }}>
                ({counts.projectSubmissionsWaitingForEvaluation} Need Evaluation Comment)
              </span>
            )}
          </div>
        </div>
      </div>

      {viewType === 'assignments' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8 }}>Title</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created by</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {myAssignments.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 8 }}>{a.title}</td>
                <td style={{ padding: 8 }}>{getCreatorName(a.creator)}</td>
                <td style={{ padding: 8 }}>{a.createddate ? new Date(a.createddate).toLocaleString() : ''}</td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => window.open(`/assignment?id=${a.id}&mode=previewWithAnswers`, '_blank')}
                      style={{ padding: '6px 16px', borderRadius: 4, background: '#1976d2', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                      Preview
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : viewType === 'projects' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8 }}>Title</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created by</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Code</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Concentration</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {myProjects.map(project => {
              const isFlagged = flaggedProjects.has(project.id);
              return (
                <tr key={project.id} style={{
                  borderBottom: '1px solid #eee',
                  background: isFlagged ? '#fff3f3' : 'transparent',
                  borderLeft: isFlagged ? '4px solid #d32f2f' : 'none'
                }}>
                  <td style={{ padding: 8 }}>
                    {project.title}
                    {isFlagged && (
                      <span style={{ color: '#d32f2f', fontWeight: 600, marginLeft: 8, fontSize: '12px' }}>
                        🚩 FLAGGED
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 8 }}>{getCreatorName(project.creator)}</td>
                  <td style={{ padding: 8 }}>{project.code}</td>
                  <td style={{ padding: 8 }}>{project.concentration}</td>
                  <td style={{ padding: 8 }}>{project.createddate ? new Date(project.createddate).toLocaleString() : ''}</td>
                  <td style={{ padding: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => window.open(`/projects-directory?command=preview&project_code=${project.code}`, '_blank')}
                        style={{ padding: '6px 16px', borderRadius: 4, background: '#1976d2', color: 'white', border: 'none', cursor: 'pointer' }}
                      >
                        Preview
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : viewType === 'projectSubmissions' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {counts.projectSubmissionsWaitingForEvaluation > 0 && (
            <div style={{
              padding: '12px 16px',
              marginBottom: 8,
              backgroundColor: '#fff8e1',
              border: '1px solid #ffc107',
              borderRadius: 8,
              color: '#5d4037',
              fontSize: 14
            }}>
              <strong>There are projects that need an evaluation comment.</strong> Despite students completing the assignments, they will not be awarded any hours until you add a comment on the project of type Evaluation.
            </div>
          )}
          {(() => {
            const psNeedsEval = projectSubmissions.filter(ps => evaluationRequiredSubmissions.has(ps.id)).length;
            const psInProgress = projectSubmissions.filter(ps => ps.status === 'started').length;
            const psCompleted = projectSubmissions.filter(ps => ps.status === 'complete').length;
            const psFiltered = projectSubmissions.filter(ps => {
              if (projectSubFilter === 'needsEvaluation') return evaluationRequiredSubmissions.has(ps.id);
              if (projectSubFilter === 'inProgress') return ps.status === 'started';
              if (projectSubFilter === 'completed') return ps.status === 'complete';
              return true;
            });
            return (
              <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 500, color: '#555', fontSize: 14 }}>Filters:</span>
            <button
              onClick={() => setProjectSubFilter('all')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'all' ? '2px solid #666' : '1px solid #ccc',
                background: projectSubFilter === 'all' ? '#f5f5f5' : '#fff',
                color: projectSubFilter === 'all' ? '#333' : '#666',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'all' ? 600 : 400
              }}
            >
              All ({projectSubmissions.length})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'needsEvaluation' ? 'all' : 'needsEvaluation')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'needsEvaluation' ? '2px solid #f57c00' : '1px solid #ccc',
                background: projectSubFilter === 'needsEvaluation' ? '#fff3e0' : '#fff',
                color: projectSubFilter === 'needsEvaluation' ? '#e65100' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'needsEvaluation' ? 600 : 400
              }}
            >
              Filter to Those that need Evaluation comment ({psNeedsEval})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'inProgress' ? 'all' : 'inProgress')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'inProgress' ? '2px solid #f57c00' : '1px solid #ccc',
                background: projectSubFilter === 'inProgress' ? '#fff3e0' : '#fff',
                color: projectSubFilter === 'inProgress' ? '#e65100' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'inProgress' ? 600 : 400
              }}
            >
              In Progress ({psInProgress})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'completed' ? 'all' : 'completed')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'completed' ? '2px solid #388e3c' : '1px solid #ccc',
                background: projectSubFilter === 'completed' ? '#e8f5e9' : '#fff',
                color: projectSubFilter === 'completed' ? '#2e7d32' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'completed' ? 600 : 400
              }}
            >
              Completed ({psCompleted})
            </button>
            <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
              Showing {psFiltered.length}
            </span>
          </div>
          {psFiltered.map((ps, index) => {
            let statusColor = '#333';
            let statusText = ps.status || 'Unknown';
            const needsEvaluation = evaluationRequiredSubmissions.has(ps.id);

            if (ps.status === 'complete') {
              statusColor = '#388e3c';
              statusText = 'Completed';
            } else if (ps.status === 'started') {
              statusColor = '#f57c00';
              statusText = 'In Progress';
            }

            return (
              <div key={ps.id} style={{
                display: 'flex',
                alignItems: 'flex-start',
                border: needsEvaluation ? '2px solid #f57c00' : '1px solid #e0e0e0',
                borderRadius: 8,
                padding: 16,
                background: needsEvaluation ? '#fff8e1' : '#fafbfc',
                boxShadow: needsEvaluation ? '0 2px 8px rgba(245,124,0,0.2)' : '0 1px 2px rgba(0,0,0,0.03)',
                position: 'relative'
              }}>
                {needsEvaluation && (
                  <div style={{
                    position: 'absolute',
                    top: -8,
                    right: 16,
                    background: '#f57c00',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    Evaluation Comment Required
                  </div>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', minWidth: 28, marginRight: 8 }}>{index + 1}.</span>
                <div style={{ marginRight: 16, marginTop: 2, cursor: 'pointer', transition: 'transform 0.1s', display: 'flex', alignItems: 'center' }}
                  title="View Project Submission"
                  onClick={() => window.open(`/projects-directory?command=evaluate&projectSubmission=${ps.id}`, '_blank')}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span role="img" aria-label="project" style={{ fontSize: 28, color: '#4caf50' }}>📋</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2, color: '#222' }}>
                    {ps.projecttitle || 'Untitled Project'}
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>
                    <span style={{ color: statusColor, fontWeight: 500 }}>Status: {statusText}</span>
                    {ps.type && (
                      <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                        (Type: {ps.type})
                      </span>
                    )}
                    {ps.hoursreceived && (
                      <span style={{ color: '#11AF07', marginLeft: 8, fontSize: 13, fontWeight: 500 }}>
                        ({ps.hoursreceived} hours received)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    Student: {ps.studentname || 'Unknown Student'}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Project creator: {getCreatorName(ps.project_creator)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Started: {ps.createddate ? new Date(ps.createddate).toLocaleString() : ''}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Updated: {ps.updateddate ? new Date(ps.updateddate).toLocaleString() : ''}</div>
                </div>
              </div>
            );
          })}
              </>
            );
          })()}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {counts.submissionsRequiringGrading > 0 && (
            <div style={{
              padding: '12px 16px',
              marginBottom: 8,
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: 8,
              color: '#b71c1c',
              fontSize: 14
            }}>
              <strong>There are assignments that require grading.</strong> They have questions like upload or short answers that must be assigned points to complete assessment-type assignments. (Surveys do not need grading regardless of question type.)
            </div>
          )}
          {(() => {
            const asRequireGrading = assignmentSubmissions.filter(s => s.graderesult === 'needsEvaluation').length;
            const asSurvey = assignmentSubmissions.filter(s => s.graderesult === 'not-required').length;
            const asGraded = assignmentSubmissions.filter(s => s.graderesult !== 'not-required').length;
            const asFiltered = assignmentSubmissions.filter(s => {
              if (assignmentSubFilter === 'requireGrading') return s.graderesult === 'needsEvaluation';
              if (assignmentSubFilter === 'survey') return s.graderesult === 'not-required';
              if (assignmentSubFilter === 'graded') return s.graderesult !== 'not-required';
              return true;
            });
            return (
              <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 500, color: '#555', fontSize: 14 }}>Filters:</span>
            <button
              onClick={() => setAssignmentSubFilter('all')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'all' ? '2px solid #666' : '1px solid #ccc',
                background: assignmentSubFilter === 'all' ? '#f5f5f5' : '#fff',
                color: assignmentSubFilter === 'all' ? '#333' : '#666',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'all' ? 600 : 400
              }}
            >
              All ({assignmentSubmissions.length})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'requireGrading' ? 'all' : 'requireGrading')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'requireGrading' ? '2px solid #d32f2f' : '1px solid #ccc',
                background: assignmentSubFilter === 'requireGrading' ? '#ffebee' : '#fff',
                color: assignmentSubFilter === 'requireGrading' ? '#b71c1c' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'requireGrading' ? 600 : 400
              }}
            >
              Filter to assignments that Require Grading ({asRequireGrading})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'survey' ? 'all' : 'survey')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'survey' ? '2px solid #1976d2' : '1px solid #ccc',
                background: assignmentSubFilter === 'survey' ? '#e3f2fd' : '#fff',
                color: assignmentSubFilter === 'survey' ? '#0d47a1' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'survey' ? 600 : 400
              }}
            >
              Survey Assignments ({asSurvey})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'graded' ? 'all' : 'graded')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'graded' ? '2px solid #388e3c' : '1px solid #ccc',
                background: assignmentSubFilter === 'graded' ? '#e8f5e9' : '#fff',
                color: assignmentSubFilter === 'graded' ? '#2e7d32' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'graded' ? 600 : 400
              }}
            >
              Graded Assignments ({asGraded})
            </button>
            <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
              Showing {asFiltered.length}
            </span>
          </div>
          {asFiltered.map((s, index) => {
            let notice = '';
            let noticeColor = '#333';
            const studentName = s.emaildata && typeof s.emaildata === 'string'
              ? (() => { try { return JSON.parse(s.emaildata).studentName || 'Student'; } catch { return 'Student'; } })()
              : (s.emaildata?.studentName || 'Student');
            if (s.graderesult === 'needsEvaluation') {
              notice = `REQUIRES GRADING for ${studentName}`;
              noticeColor = '#d32f2f';
            } else if (s.graderesult === 'not-required') {
              notice = `SURVEY--NO GRADING REQUIRED for ${studentName}`;
              noticeColor = '#1976d2';
            } else if (typeof s.graderesult === 'string' && s.graderesult.includes('%')) {
              notice = `${s.graderesult} for ${studentName}`;
              noticeColor = '#388e3c';
            } else {
              notice = `Status: ${s.graderesult || 'N/A'} for ${studentName}`;
              noticeColor = '#333';
            }
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, background: '#fafbfc', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', minWidth: 28, marginRight: 8 }}>{index + 1}.</span>
                <div style={{ marginRight: 16, marginTop: 2, cursor: 'pointer', transition: 'transform 0.1s', display: 'flex', alignItems: 'center' }}
                  title="View Submission"
                  onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span role="img" aria-label="assignment" style={{ fontSize: 28, color: '#fbc02d' }}>📝</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2, color: '#222' }}>{s.assignmenttitle || s.title || 'Untitled'}</div>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>
                    <span style={{ color: noticeColor, fontWeight: 500 }}>{notice}</span>
                    {s.projecttitle && (
                      <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                        (P: {s.projecttitle})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Assignment creator: {getCreatorName(s.assignment_creator)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Created : {s.createddate ? new Date(s.createddate).toLocaleString() : ''}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Updated : {s.updateddate ? new Date(s.updateddate).toLocaleString() : ''}</div>
                </div>
              </div>
            );
          })}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AdminDirectory;
